import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, MessageSquare } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getSupportMessages, createSupportReply } from "@/services/authService";
import { tenantKeys } from "@/lib/queryKeys";
import { formatDisplayDate } from "@/lib/dateFormat";
import toast from "react-hot-toast";

export function OnboardingSupportReplyCard(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [replyText, setReplyText] = useState("");

  const { data: messages = [] } = useQuery({
    queryKey: tenantKeys.supportMessages(),
    queryFn: getSupportMessages,
  });

  const lastSupportMessage = [...messages]
    .reverse()
    .find((m) => m.direction === "support");

  const sendReplyMutation = useMutation({
    mutationFn: (message: string) => createSupportReply(message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.supportMessages() });
      setReplyText("");
      toast.success(t("onboarding.support.replySuccess"));
    },
    onError: () => toast.error(t("onboarding.support.replyError")),
  });

  if (lastSupportMessage == null) return <></>;

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-4 w-4" />
          {t("onboarding.support.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-md border border-border bg-background px-3 py-2 text-sm">
          <p className="mb-1 text-muted-foreground">
            {formatDisplayDate(lastSupportMessage.createdAt)}
          </p>
          <p className="whitespace-pre-wrap text-foreground">
            {lastSupportMessage.body}
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="onboarding-support-reply">
            {t("onboarding.support.replyLabel")}
          </Label>
          <Textarea
            id="onboarding-support-reply"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={t("onboarding.support.replyPlaceholder")}
            rows={2}
            disabled={sendReplyMutation.isPending}
            className="resize-none"
          />
          <Button
            type="button"
            size="sm"
            onClick={() => {
              if (replyText.trim())
                sendReplyMutation.mutate(replyText.trim());
            }}
            disabled={
              !replyText.trim() || sendReplyMutation.isPending
            }
          >
            {sendReplyMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {t("onboarding.support.reply")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
